import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useContainerQuery } from "@transfr-inc/dashboard-components/container-query";
import { useEffect, useMemo, useState } from "react";

import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionHeaderName,
  Loader,
  Notification,
  NotificationType,
  TextTooltip,
} from "@transfr-inc/dashboard-components";
import { getLearnerTerm } from "@transfr-inc/dashboard-components/constants";
import { Button } from "@transfr-inc/dashboard-components/forms";
import { formatTimestap } from "@transfr-inc/dashboard-components/utils";

import { AwardsProgressTracker } from "@components/progress-tracker";
import { ExportStatus, doExportData } from "@lib/export-utils";
import { generateTraineeGradebookCSVData } from "@lib/gradebook-export.utils";
import { useApiRequest } from "@lib/hooks";
import container from "@src/container";
import { CourseUnitAccordion } from "@views/my-work/components/course/course-unit-accordion";
import {
  findCurrentObjective,
  getGroupIds,
  mapCompletedAndLockedData,
  mapCredentialData,
  mapMasteries,
} from "@views/my-work/components/course/course.utils";
import { UnitLessonAccordion as CourseLessonAccordion } from "@views/my-work/components/course/unit-lesson-accordion";

import { ActivitiesToolbar } from "./trainee.classroom.prog.toolbar";

import "./trainee.classroom.prog.tab.scss";

const query = {
  "small-panel": {
    minWidth: 320,
    maxWidth: 850,
  },
  "medium-panel": {
    minWidth: 700,
    maxWidth: 900,
  },
  "large-panel": {
    minWidth: 900,
  },
};

export const TraineeClassroomProgTab = ({ trainee, classroom }) => {
  const { classroomService, courseService, insightsService } = container;
  const [params, containerRef] = useContainerQuery(query);
  const [courseData, setCourseData] = useState();
  const [filteredCourseData, setFilteredCourseData] = useState();
  const [exportStatus, setExportStatus] = useState(ExportStatus.complete);
  const [notification, setNotification] = useState();
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [searchedActivityIds, setSearchedActivityIds] = useState(null);
  const [openUnits, setOpenUnits] = useState(new Set());
  const [openLessons, setOpenLessons] = useState(new Set());

  const [currentObjective, setCurrentObjective] = useState({
    id: null,
    parentId: null,
  });

  const { courseId, classroomId } = classroom;

  const { loading: loadingInstructors, response: instructors = [] } =
    useApiRequest(() =>
      classroomService.getClassroomInstructors(classroom.classroomId)
    );

  const { response: courseDataResponse, loading: courseDataLoading } =
    useApiRequest(() => courseService.getCourseDetails(courseId));

  const {
    response: courseCredentials,
    loading: courseCredentialsLoading,
    sendRequest: getCourseCredentials,
  } = useApiRequest(() => courseService.getCourseCredentials(courseId), false);

  const { loading: loadingCourseMastery, response: courseMastery } =
    useApiRequest(() =>
      insightsService.getCourseMastery(trainee.userId, courseId)
    );

  const {
    loading: loadingActivityDependencies,
    response: activityDependencies,
  } = useApiRequest(() => courseService.getActivityDependencies(courseId));

  const { mainCredential: baseMainCredential, courseInfo: baseCourseInfo } =
    useMemo(() => {
      if (!courseCredentials || !courseDataResponse)
        return { mainCredential: null, courseInfo: [] };

      const { mainCredential, courseInfo } = mapCredentialData(
        courseCredentials,
        courseDataResponse
      );

      return {
        mainCredential,
        courseInfo,
      };
    }, [courseCredentials, courseDataResponse]);

  const allGroupIds = useMemo(() => {
    return getGroupIds(baseMainCredential, baseCourseInfo);
  }, [baseCourseInfo, baseMainCredential]);

  const {
    sendRequest: getCourseCredentialsSummaryStats,
    response: courseCredentialsSummaryStats,
    loading: courseCredentialsSummaryStatsLoading,
  } = useApiRequest(
    () =>
      insightsService.getCourseCredentialsSummaryStats(
        classroomId,
        allGroupIds,
        [trainee.userId]
      ),
    false
  );

  useEffect(() => {
    if (allGroupIds?.length > 0) {
      getCourseCredentialsSummaryStats();
    }
  }, [allGroupIds]);

  useEffect(() => {
    if (courseId) {
      getCourseCredentials();
    }
  }, [courseId]);

  const { mainCredential, courseInfo, hasAwards } = useMemo(() => {
    const hasAwards =
      baseMainCredential || (baseCourseInfo && baseCourseInfo.length > 0);
    if (!courseCredentialsSummaryStats) {
      return {
        mainCredential: baseMainCredential,
        courseInfo: baseCourseInfo,
        hasAwards: hasAwards,
      };
    }

    const updatedCourseInfo = baseCourseInfo.map((info) => ({
      ...info,
      totalAwarded: courseCredentialsSummaryStats[String(info?.groupId)] || 0,
    }));

    const updatedMainCredential = baseMainCredential
      ? {
          ...baseMainCredential,
          totalAwarded:
            courseCredentialsSummaryStats[
              String(baseMainCredential?.groupId)
            ] || 0,
        }
      : null;

    return {
      mainCredential: updatedMainCredential,
      courseInfo: updatedCourseInfo,
      hasAwards: hasAwards,
    };
  }, [courseCredentialsSummaryStats, baseMainCredential, baseCourseInfo]);

  useEffect(() => {
    if (
      !loadingCourseMastery &&
      !courseDataLoading &&
      !loadingActivityDependencies &&
      !loadingInstructors
    ) {
      const masteries = mapMasteries(courseMastery);
      if (courseDataResponse && masteries) {
        const updatedCourse = mapCompletedAndLockedData(
          structuredClone(courseDataResponse),
          masteries,
          activityDependencies ?? {},
          courseId
        );

        const { currentObjectiveId, parentId } =
          findCurrentObjective(updatedCourse);

        setCurrentObjective({ id: currentObjectiveId, parentId });
        setOpenUnits(new Set([parentId]));
        setOpenLessons(new Set([currentObjectiveId]));

        setCourseData(updatedCourse);
      }
    }
  }, [
    courseMastery,
    courseDataResponse,
    loadingCourseMastery,
    courseDataLoading,
    loadingActivityDependencies,
    loadingInstructors,
  ]);

  useEffect(() => {
    if (!courseData) return;

    let result = courseData;

    if (selectedActivity) {
      let activityIdsToKeep = selectedActivity
        ? [selectedActivity.id]
        : undefined;
      result = filterCourseDataByIds(result, activityIdsToKeep);
    }

    if (searchedActivityIds) {
      const activityIdsToKeep = new Set(searchedActivityIds);
      result = filterCourseDataByIds(result, activityIdsToKeep);
    }

    setFilteredCourseData(result);
  }, [courseData, selectedActivity, searchedActivityIds]);

  useEffect(() => {
    if (filteredCourseData && (selectedActivity || searchedActivityIds)) {
      const unitIds = new Set();
      const lessonIds = new Set();

      for (const unit of filteredCourseData.units) {
        unitIds.add(unit.id);
        for (const lesson of unit.lessons) {
          lessonIds.add(lesson.id);
        }
      }

      setOpenUnits(unitIds);
      setOpenLessons(lessonIds);
    } else {
      setOpenUnits(new Set([currentObjective.parentId]));
      setOpenLessons(new Set([currentObjective.id]));
    }
  }, [filteredCourseData, selectedActivity, searchedActivityIds]); // More accurate dependencies

  const allCourseActivities = useMemo(() => {
    if (!courseData?.lessons?.length && !courseData?.units?.length) return [];
    if (courseData?.lessons?.length) {
      return courseData.lessons.flatMap((lesson) => {
        return lesson.activities.map((activity) => {
          return {
            ...activity,
            lessonTitle: lesson.title,
          };
        });
      });
    } else if (courseData?.units?.length) {
      return courseData.units.flatMap((unit) =>
        unit.lessons.flatMap((lesson) => {
          return lesson.activities.map((activity) => {
            return {
              ...activity,
              unitTitle: unit.title,
              lessonTitle: lesson.title,
            };
          });
        })
      );
    }
  }, [courseData]);

  const activityOptions = useMemo(() => {
    if (!allCourseActivities) return [];
    return [
      ...new Map(allCourseActivities.map((item) => [item.id, item])).values(),
    ];
  }, [allCourseActivities]);

  const activitiesForSearch = useMemo(() => {
    if (selectedActivity) {
      return allCourseActivities.filter(
        (activity) => activity.id === selectedActivity.id
      );
    }
    return allCourseActivities;
  }, [allCourseActivities, selectedActivity]);

  useEffect(() => {
    if (notification) {
      const timoutId = setTimeout(() => setNotification(), 3000);
      return () => clearTimeout(timoutId);
    }
  }, [notification]);

  const handleActivitySearch = ({ resourceIds, term }) => {
    if (term === "" && resourceIds?.length === 0) {
      setSearchedActivityIds(null);
    } else {
      setSearchedActivityIds(resourceIds);
    }
  };

  const filterCourseDataByIds = (data, ids) => {
    if (ids === null || ids === undefined) {
      return data;
    }

    const activityIdsToKeep = new Set(ids);

    const filteredUnits = data.units
      .map((unit) => {
        const filteredLessons = unit.lessons
          .map((lesson) => {
            const filteredActivities = lesson.activities.filter((activity) =>
              activityIdsToKeep.has(activity.id)
            );

            if (filteredActivities.length > 0) {
              return { ...lesson, activities: filteredActivities };
            }
            return null;
          })
          .filter(Boolean);

        if (filteredLessons.length > 0) {
          return { ...unit, lessons: filteredLessons };
        }
        return null;
      })
      .filter(Boolean);

    return {
      ...data,
      units: filteredUnits,
    };
  };

  const onExportData = async () => {
    setExportStatus(ExportStatus.creating);

    try {
      if (!courseDataResponse || !courseData || !allCourseActivities?.length) {
        console.error("No data available for export");
        setExportStatus(ExportStatus.error);
        return;
      }

      const programName = courseDataResponse?.title || "";

      const csvData = generateTraineeGradebookCSVData(
        allCourseActivities,
        programName
      );

      const fileName = `${trainee.firstName}_${
        trainee.lastName
      }-${formatTimestap()}`;
      await doExportData(fileName, csvData);
      setExportStatus(ExportStatus.complete);
    } catch {
      setExportStatus(ExportStatus.error);
      setNotification({
        type: NotificationType.error,
        message: "Something went wrong while exporting the data.",
        closable: true,
      });
    }
  };

  const loadingCredentials =
    courseCredentialsLoading ||
    courseCredentialsSummaryStatsLoading ||
    courseDataLoading;

  const unitsComponent = () => {
    return (
      <div className="course-units-container">
        {filteredCourseData?.units?.map((unit, index) => (
          <CourseUnitAccordion
            key={unit.id}
            unit={unit}
            expandedLessons={openLessons}
            unitIndex={index + 1}
            isExpanded={openUnits.has(unit.id)}
            courseId={courseId}
            courseName={courseData.title}
            responsiveStyles={params}
            currentObjectiveId={currentObjective.id}
            credentialData={{ mainCredential, courseInfo }}
            completionMap={courseData.completionMap}
            classroomId={classroomId}
            courseData={courseData}
            activityDependencies={activityDependencies}
            preqEnabled={false}
            isTraineePage={true}
          />
        ))}
      </div>
    );
  };

  const lessonsComponent = () => {
    return (
      <div>
        {filteredCourseData?.lessons?.map((lesson, index) => (
          <CourseLessonAccordion
            key={lesson.id}
            lesson={lesson}
            lessonIndex={index + 1}
            responsiveStyles={params}
            courseId={courseId}
            isExpanded={openLessons.has(lesson.id)}
            courseName={courseData.title}
            unitName={courseData.title}
            credentialData={{ mainCredential, courseInfo }}
            completionMap={courseData.completionMap}
            classroomId={classroomId}
            courseData={courseData}
            activityDependencies={activityDependencies}
            preqEnabled={false}
            isTraineePage={true}
          />
        ))}
      </div>
    );
  };

  return (
    <div className="trainee-classroom-prog-tab">
      <div className="instructor-name-container">
        Instructors:
        {loadingInstructors ? (
          <Loader className="btn-loader"></Loader>
        ) : (
          instructors.map((instructor) => (
            <div className="instructor-name" key={instructor.id}>
              {instructor.firstName} {instructor.lastName}
            </div>
          ))
        )}
      </div>
      {hasAwards && (
        <Accordion
          className="awards-accordion"
          isOpen={true}
          hideExpandProperties={false}
        >
          <div className="awards-accordion-header">
            <AccordionHeader>
              <AccordionHeaderName>
                <span>Awards</span>
                <TextTooltip
                  text={`Awards earned by the ${getLearnerTerm({
                    lowercase: true,
                  })}`}
                >
                  <FontAwesomeIcon icon={"fa-regular fa-circle-info"} />
                </TextTooltip>
              </AccordionHeaderName>
            </AccordionHeader>
          </div>
          <AccordionBody>
            <div className="awards-accordion-body">
              {loadingCredentials ? (
                <Loader overlay fixed></Loader>
              ) : (
                <AwardsProgressTracker
                  mainCredential={mainCredential}
                  courseInfo={courseInfo}
                />
              )}
            </div>
          </AccordionBody>
        </Accordion>
      )}
      {!loadingCredentials && (
        <div className="activities-menu-container">
          <div className="activities-menu-left">
            <div className="activities-menu-header">Activities</div>
            {courseData && (
              <ActivitiesToolbar
                onOptionSelected={setSelectedActivity}
                selected={selectedActivity}
                activityOptions={activityOptions}
                data={activitiesForSearch}
                onSearchTermChange={handleActivitySearch}
                filters={{}}
              />
            )}
          </div>
          <div className="activities-menu-right">
            <div>
              <Button
                primary
                icon={["fa-regular", "file-export"]}
                onClick={onExportData}
              >
                {exportStatus === ExportStatus.creating
                  ? "Exporting..."
                  : "Export"}
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="flavor-text">
        Press a score to make changes or mark the activity exempt for a student.
      </div>
      <div className="activities-container" ref={containerRef}>
        {loadingCredentials ? (
          <Loader overlay fixed></Loader>
        ) : (
          <>
            {filteredCourseData?.units && unitsComponent()}
            {filteredCourseData?.lessons && lessonsComponent()}
          </>
        )}
      </div>
      <div className="notification-loader-container">
        {notification && (
          <Notification
            type={notification.type}
            icon={notification.icon}
            closable
            onClose={() => setNotification()}
          >
            {notification.message}
          </Notification>
        )}
      </div>
    </div>
  );
};
